/**
 * Analytics & Conversion Tracking Utility
 * Supports Google Ads (gtag), Meta Pixel (fbq), TikTok Pixel (ttq).
 * Tracking is ONLY initialized after cookie consent.
 */

const GTM_ID = import.meta.env.VITE_GOOGLE_ADS_ID || '';
const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';
const TIKTOK_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID || '';

/* ------------------------------------------------------------------ */
/*  DataLayer                                                          */
/* ------------------------------------------------------------------ */

export function pushToDataLayer(eventName: string, payload?: Record<string, unknown>) {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: eventName, ...payload });
}

/* ------------------------------------------------------------------ */
/*  Google Ads / gtag                                                  */
/* ------------------------------------------------------------------ */

let gtagLoaded = false;

function ensureGtag() {
  if (gtagLoaded || !GTM_ID) return;
  gtagLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GTM_ID}`;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag('js', new Date());
  gtag('config', GTM_ID, { send_page_view: false });
}

/* ------------------------------------------------------------------ */
/*  Meta Pixel / fbq                                                   */
/* ------------------------------------------------------------------ */

let pixelLoaded = false;

function ensurePixel() {
  if (pixelLoaded || !PIXEL_ID) return;
  pixelLoaded = true;

  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${PIXEL_ID}');
  `;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
}

/* ------------------------------------------------------------------ */
/*  TikTok Pixel / ttq                                                 */
/* ------------------------------------------------------------------ */

let tiktokLoaded = false;

function ensureTikTok() {
  if (tiktokLoaded || !TIKTOK_ID) return;
  tiktokLoaded = true;

  const script = document.createElement('script');
  script.innerHTML = `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
      ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
      ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
      for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
      ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
      ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;
      ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;
      ttq._o=ttq._o||{};ttq._o[e]=n||{};
      var s=document.createElement("script");s.type="text/javascript";s.async=!0;s.src=r+"?sdkid="+e+"&lib="+t;
      var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(s,a)};
      ttq.load('${TIKTOK_ID}');
      ttq.page();
    }(window, document, 'ttq');
  `;
  document.head.appendChild(script);
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/** Call once after cookie consent is accepted */
export function initAnalytics() {
  ensureGtag();
  ensurePixel();
  ensureTikTok();
}

/** Track a page view (call on route change) */
export function trackPageView(path: string, title?: string) {
  pushToDataLayer('page_view', { page_path: path, page_title: title });

  if ((window as any).gtag && GTM_ID) {
    (window as any).gtag('config', GTM_ID, { page_path: path, page_title: title });
  }
  if ((window as any).fbq) {
    (window as any).fbq('track', 'PageView');
  }
  if ((window as any).ttq) {
    (window as any).ttq.page();
  }
}

/** Track viewing a specific program */
export function trackViewProgram(programName: string, location: string) {
  pushToDataLayer('view_program', { program_name: programName, location });

  if ((window as any).gtag) {
    (window as any).gtag('event', 'view_item', {
      event_category: 'Programs',
      event_label: programName,
      items: [{ item_name: programName, location_id: location }],
    });
  }
  if ((window as any).fbq) {
    (window as any).fbq('track', 'ViewContent', { content_name: programName });
  }
  if ((window as any).ttq) {
    (window as any).ttq.track('ViewContent', { content_name: programName });
  }
}

/** Track a lead / form submission conversion */
export function trackLead(source: string, value?: number) {
  pushToDataLayer('form_submit', { form_type: source, value: value || 0 });

  if ((window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: GTM_ID ? `${GTM_ID}/lead` : undefined,
      event_category: 'Lead',
      event_label: source,
      value: value || 0,
    });
    (window as any).gtag('event', 'generate_lead', {
      event_category: 'engagement',
      event_label: source,
    });
  }
  if ((window as any).fbq) {
    (window as any).fbq('track', 'Lead', { content_name: source });
  }
  if ((window as any).ttq) {
    (window as any).ttq.track('SubmitForm', { content_name: source });
  }
}

/** Track newsletter subscription */
export function trackSubscribe() {
  pushToDataLayer('subscribe', { method: 'newsletter' });

  if ((window as any).gtag) {
    (window as any).gtag('event', 'sign_up', { method: 'newsletter' });
  }
  if ((window as any).fbq) {
    (window as any).fbq('track', 'Subscribe');
  }
  if ((window as any).ttq) {
    (window as any).ttq.track('Subscribe');
  }
}

/** Track consultation booking click */
export function trackBooking() {
  pushToDataLayer('click_booking_cta', { event_category: 'Booking' });

  if ((window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: GTM_ID ? `${GTM_ID}/booking` : undefined,
      event_category: 'Booking',
      event_label: 'consultation',
    });
  }
  if ((window as any).fbq) {
    (window as any).fbq('track', 'Schedule');
  }
  if ((window as any).ttq) {
    (window as any).ttq.track('PlaceAnOrder', { content_name: 'consultation' });
  }
}

/** Track CTA clicks */
export function trackCTAClick(ctaName: string) {
  pushToDataLayer('click_cta', { cta_name: ctaName });
}

/** Track language change */
export function trackLanguageChange(language: string) {
  pushToDataLayer('language_change', { language });
}
